import React, { useEffect, useState } from 'react';
import { Box, Typography, Avatar, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import { TitleScreen, SubTitleScreen, PrimaryButton } from '../../SystemDesign/Styles';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useAuth } from '../../Context/AuthContext';
import { useGetUserQuery } from '../../services/SkillsManagement';
// Cards view removed; show QuizEvaluationList for Written view instead
import QuizEvaluationList from '../Quiz_Evaluation/QuizEvaluationListEmployee';
import ViewToggle from '../../SystemDesign/ViewToggle';
import MyEvaluationList from './MyEvaluationList';
import AddEvaluation from '../Evaluations/AddEvaluation';
// Pagination and cards view removed; re-introduce search for Oral (table) view and place it next to the ViewToggle
import SearchInput from '../../SystemDesign/SearchInput';
import LoaderSpinner from '../../SystemDesign/LoaderSpinner';
import AddIcon from '@mui/icons-material/Add';

const MyEvaluation = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [openAdd, setOpenAdd] = useState(false);
  const [viewMode, setViewMode] = useState('cards');
  const { user } = useAuth();

  // Safely derive user fields
  const firstName = user?.profile?.firstName || user?.firstName || '';
  const lastName = user?.profile?.lastName || user?.lastName || '';
  const email = user?.email || user?.profile?.email || '';
  const employeeCode = user?.employeeCode || user?.username || user?.id || '';
  const roleLabel = user?.role?.type || user?.role?.name || user?.role || '';
  // We no longer fetch poste here to avoid invalid requests

  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();

  // Load user details (evaluations, poste, requirements) for the current employee
  const employeeId = Number(user?.id);
  const shouldSkipUser = !Number.isFinite(employeeId);
  const { data: userResp, isLoading: userLoading, isFetching: userFetching, refetch: refetchUser } = useGetUserQuery(
    { userId: String(employeeId || '') },
    { skip: shouldSkipUser }
  );

  const employeeName = userResp?.user?.username || user?.username || '';
  const posteObj = userResp?.user?.Poste || userResp?.user?.poste || null;
  const posteId = posteObj?.posteId || userResp?.user?.posteId || userResp?.posteId || user?.posteId || null;
  const posteName = posteObj?.name || '';

  // Evaluations come from getUser (includes poste + skillRequirements)
  // IMPORTANT: Restrict to self-evaluations only (employee_id matches current user id)
  const evaluations = React.useMemo(() => {
    const list = Array.isArray(userResp?.user?.evaluations) ? userResp.user.evaluations : [];
    const eid = Number(employeeId);
    if (!Number.isFinite(eid)) return [];
    return list.filter((e) => String(e?.employee_id ?? e?.employee?.id) === String(eid));
  }, [userResp?.user?.evaluations, employeeId]);

  // Requirements list comes from user's Poste payload
  const requirementsList = (
    userResp?.user?.Poste?.skillRequirements
    || userResp?.user?.poste?.skillRequirements
    || []
  );
  const reqBySkillId = React.useMemo(() => {
    const map = new Map();
    if (Array.isArray(requirementsList)) {
      requirementsList.forEach((r) => {
        const sId = r?.skill_id || r?.skillId || r?.skill?.skill_id || r?.skill?.id;
        if (!sId) return;
        map.set(Number(sId), {
          min: r?.min_score ?? r?.required_min_score ?? r?.minScore ?? null,
          max: r?.max_score ?? r?.required_max_score ?? r?.maxScore ?? null,
          level: r?.required_level ?? r?.requiredLevel ?? null,
        });
      });
    }
    return map;
  }, [requirementsList]);

  // Enrich evaluations with employee/poste and required fields
  const evaluationsHydrated = React.useMemo(() => {
    return (evaluations || []).map((e) => {
      const sId = Number(e?.skill_id || e?.skill?.id);
      const req = reqBySkillId.get(sId) || {};
      const mergedPoste = e?.poste || posteObj || null;
      return {
        ...e,
        employeeName: e?.employee?.username || employeeName || e?.employeeName,
        employee: e?.employee || (employeeName ? { username: employeeName, id: employeeId } : undefined),
        posteName: e?.poste?.name || mergedPoste?.name || e?.posteName,
        poste: mergedPoste,
        required_min_score: e?.required_min_score ?? e?.min_score ?? req.min ?? null,
        required_max_score: e?.required_max_score ?? e?.max_score ?? req.max ?? null,
        required_level: e?.required_level ?? e?.requiredLevel ?? req.level ?? null,
      };
    });
  }, [evaluations, employeeName, employeeId, posteObj, reqBySkillId]);

  // Query params (client-side) similar to DisciplineList.jsx
  const [queryParams, setQueryParams] = useState(() => {
    const saved = localStorage.getItem('my-evaluations-rowsPerPage');
    const rowsPerPage = saved ? parseInt(saved, 10) : 10;
    return {
      filters: '',
      sorting: 'date_desc', // default
      pagination: JSON.stringify({ currentPage: 1, rowsPerPage }),
      search: ''
    };
  });

  const paginationState = (() => {
    try {
      return JSON.parse(queryParams.pagination || '{}');
    } catch {
      return { currentPage: 1, rowsPerPage: 10 };
    }
  })();

  const applyFilters = (filters = null, paginationInfo = null, sortingInfo = null, searchValue = null) => {
    const newParams = { ...queryParams };
    if (filters !== null) newParams.filters = typeof filters === 'string' ? filters : JSON.stringify(filters);
    if (paginationInfo !== null) {
      const current = newParams.pagination ? JSON.parse(newParams.pagination) : { currentPage: 1, rowsPerPage: 10 };
      const updated = { ...current, ...paginationInfo };
      if (paginationInfo.rowsPerPage) {
        localStorage.setItem('my-evaluations-rowsPerPage', String(paginationInfo.rowsPerPage));
      }
      newParams.pagination = JSON.stringify(updated);
    }
    if (sortingInfo !== null) {
      // In table mode, ListViewV2 provides sorting as an object; store as JSON string like RolesList
      if (viewMode === 'table') {
        newParams.sorting = sortingInfo && typeof sortingInfo === 'object'
          ? JSON.stringify(sortingInfo)
          : (sortingInfo || '');
      } else {
        newParams.sorting = sortingInfo;
      }
    }
    if (searchValue !== null) newParams.search = searchValue;
    setQueryParams(newParams);
  };

  // Normalize sorting when switching views to avoid JSON.parse errors in ListViewV2
  useEffect(() => {
    setQueryParams((prev) => {
      const current = { ...prev };
      if (viewMode === 'table') {
        // ListViewV2 expects sorting to be a JSON string or empty string; we pass empty string
        if (current.sorting !== '') {
          current.sorting = '';
        }
      } else {
        // Cards view: ensure we have a simple default string sorting
        if (!current.sorting || typeof current.sorting !== 'string') {
          current.sorting = 'date_desc';
        }
      }
      return current;
    });
  }, [viewMode]);

  // Cards view removed: no local search/filter/pagination derived values here

  // Sanitize sorting for ListViewV2 to prevent JSON.parse errors on first mount
  const tableQueryParams = React.useMemo(() => {
    if (viewMode !== 'table') return queryParams;
    const s = queryParams?.sorting;
    if (!s) return { ...queryParams, sorting: '' };
    if (typeof s !== 'string') return { ...queryParams, sorting: '' };
    try {
      JSON.parse(s);
      return queryParams; // already a JSON string
    } catch {
      return { ...queryParams, sorting: '' };
    }
  }, [queryParams, viewMode]);

  return (
    <Box p={3}>
      {/* Page header (title + subtitle) with Add button on the right, like PostesList */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <div>
          <Typography sx={TitleScreen(colors)}>My Evaluations</Typography>
          <Typography sx={SubTitleScreen(colors)}>
            Manage and review your evaluations.
          </Typography>
        </div>
        <div>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setOpenAdd(true)}
             variant="contained"
            color="primary"
          >
            Add Evaluation
          </Button>
        </div>
      </div>
     

      {/* Profile header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 3 }}>
        <Avatar sx={{ width: 56, height: 56, bgcolor: colors.grey[200], color: colors.grey[800], fontWeight: 600 }}>
          {initials || '?'}
        </Avatar>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {firstName || lastName ? `${firstName} ${lastName}` : '—'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, color: 'text.secondary' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WorkOutlineIcon fontSize="small" />
              <Typography variant="body2">{roleLabel || '—'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <MailOutlineIcon fontSize="small" />
              <Typography variant="body2">{email || '—'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonOutlineIcon fontSize="small" />
              <Typography variant="body2">{employeeCode || '—'}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Search + View toggle on one line
          - Oral (table): search + toggle rendered here
          - Written (quiz list): toggle is rendered inside child header via headerRight
        */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
        {viewMode === 'table' && (
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <SearchInput
              placeholder="Search evaluations..."
              value={queryParams.search}
              onChange={(v) => {
                setQueryParams((prev) => ({ ...prev, search: v }));
                if (!v) applyFilters(null, { currentPage: 1 }, null, '');
              }}
              onSearch={(v) => applyFilters(null, { currentPage: 1 }, null, v)}
              debounce={300}
            />
          </Box>
        )}
        {viewMode === 'table' && (
          <Box sx={{ ml: 'auto' }}>
            <ViewToggle
              value={viewMode}
              onChange={(next) => {
                setViewMode(next);
                setQueryParams((prev) => {
                  const updated = { ...prev };
                  if (next === 'table') {
                    updated.sorting = '';
                  } else {
                    if (!updated.sorting || typeof updated.sorting !== 'string') {
                      updated.sorting = 'date_desc';
                    }
                  }
                  return updated;
                });
              }}
              cardsLabel="Written evaluation"
              tableLabel="Oral evaluation"
              label="View:"
            />
          </Box>
        )}
      </Box>
 
    {/* Loading spinner or evaluations (Oral table or Written quiz list) */}
    {(userLoading || userFetching) ? (
      <LoaderSpinner loading={userLoading || userFetching} />
    ) : (
      viewMode === 'table' ? (
        // Oral evaluations (table)
        <MyEvaluationList
          evaluations={evaluationsHydrated}
          queryParams={tableQueryParams}
          setQueryParams={setQueryParams}
          applyFilters={applyFilters}
          selfEmployeeId={employeeId}
        />
      ) : (
        // Written evaluations (quiz list for employee) with toggle passed to child header
        <QuizEvaluationList
          headerRight={(
            <ViewToggle
              value={viewMode}
              onChange={(next) => {
                setViewMode(next);
                setQueryParams((prev) => {
                  const updated = { ...prev };
                  if (next === 'table') {
                    updated.sorting = '';
                  } else {
                    if (!updated.sorting || typeof updated.sorting !== 'string') {
                      updated.sorting = 'date_desc';
                    }
                  }
                  return updated;
                });
              }}
              cardsLabel="Written evaluation"
              tableLabel="Oral evaluation"
              label="View:"
            />
          )}
        />
      )
    )}

      {/* Cards pagination removed; header search and type filter removed */}

      {/* Add Evaluation dialog (always mounted so internal quiz dialog can open after closing) */}
      <AddEvaluation
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        presetRaterTypeName="self"
        allowedRaterTypeNames={['self']}
        onSubmit={() => {
          setOpenAdd(false);
          try { refetchUser && refetchUser(); } catch (_) {}
        }}
      />
    </Box>
  );
};

export default MyEvaluation;